/**
 * EVE types — mirror cx-alm DTOs.
 *
 * Source of truth (server-side):
 *   services/cx-alm/.../presentation/dto/EveAnalysisResponse.java
 *   services/cx-alm/.../presentation/dto/TriggerEveRequest.java
 *
 * Six Basel IRRBB standard scenarios are returned in every response.
 * BigDecimal values arrive as decimal strings — format only at render time.
 */

import type { GapTriggerType } from '@/features/alm/types';

/** Basel IRRBB scenario codes — must match IrrbbScenario.java enum codes. */
export type IrrbbScenarioCode =
  | 'PARALLEL_UP_200'
  | 'PARALLEL_DOWN_200'
  | 'SHORT_RATE_UP_250'
  | 'SHORT_RATE_DOWN_250'
  | 'STEEPENER_150'
  | 'FLATTENER_150';

export interface KrdBucketDto {
  tenorLabel: string;   // "ON", "1M", "3M", "6M", "1Y", "2Y", "5Y", "10Y", "20Y", "30Y"
  dv01: string;         // BigDecimal as string [CCY per 1bp]
}

/**
 * Phase 4.2.EVE — per-instrument-type EVE contribution.
 * eveByScenario is keyed by scenario code (e.g. "PARALLEL_UP_200").
 * All BigDecimal values arrive as decimal strings — format only at render time.
 */
export interface EveInstrumentBreakdownDto {
  instrumentType:  string;
  portfolioPv:     string;
  eveByScenario:   Record<IrrbbScenarioCode, string>;
  worstCaseEve:    string;
}

export interface EveAnalysisDetail {
  resultId: string;
  tenantId: string;
  calculationDate: string;       // ISO yyyy-MM-dd
  engineVersion: string;
  triggerType: GapTriggerType;

  /** Net portfolio PV at baseline [CCY]. */
  portfolioPv: string;

  /** Per-scenario EVE = PV_baseline − PV_stressed. Negative = equity loss. */
  eveParallelUp: string;
  eveParallelDown: string;
  eveShortRateUp: string;
  eveShortRateDown: string;
  eveSteepener: string;
  eveFlattener: string;

  /** Worst-case (most negative) EVE across all six scenarios. */
  worstCaseEve: string;
  worstCaseScenario: IrrbbScenarioCode | null;
  /** worstCaseEve / |portfolioPv| as a decimal ratio. */
  worstCaseEvePct: string;

  krdBuckets: KrdBucketDto[];

  degradedMode?: boolean;

  /**
   * Phase 4.2.EVE — per-instrument-type EVE breakdown.
   * Absent for results calculated before Phase 4.2.
   */
  instrumentBreakdown?: EveInstrumentBreakdownDto[];
}

export interface TriggerEveRequest {
  calculationDate: string;       // ISO yyyy-MM-dd
  force?: boolean;
}

/** Human-readable labels for the six Basel scenarios. */
export const SCENARIO_LABELS: Record<IrrbbScenarioCode, string> = {
  PARALLEL_UP_200:    'Parallel +200bp',
  PARALLEL_DOWN_200:  'Parallel −200bp',
  SHORT_RATE_UP_250:  'Short-rate +250bp',
  SHORT_RATE_DOWN_250:'Short-rate −250bp',
  STEEPENER_150:      'Steepener ±75bp',
  FLATTENER_150:      'Flattener ±75bp',
};

/** Ordered list for table rendering. */
export const SCENARIO_ORDER: IrrbbScenarioCode[] = [
  'PARALLEL_UP_200',
  'PARALLEL_DOWN_200',
  'SHORT_RATE_UP_250',
  'SHORT_RATE_DOWN_250',
  'STEEPENER_150',
  'FLATTENER_150',
];

/** Pick the EVE value from a detail object by scenario code. */
export function eveForScenario(
  d: EveAnalysisDetail,
  scenario: IrrbbScenarioCode,
): string {
  const map: Record<IrrbbScenarioCode, string> = {
    PARALLEL_UP_200:    d.eveParallelUp,
    PARALLEL_DOWN_200:  d.eveParallelDown,
    SHORT_RATE_UP_250:  d.eveShortRateUp,
    SHORT_RATE_DOWN_250:d.eveShortRateDown,
    STEEPENER_150:      d.eveSteepener,
    FLATTENER_150:      d.eveFlattener,
  };
  return map[scenario] ?? '0';
}
